// web/src/data/follow.ts
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";

export type FollowStatus = "pending" | "approved";

function edgeId(from: string, to: string) {
  return `${from}_${to}`;
}
function resolvePrivacy(raw: any): "public" | "review" | "private" {
  const v = String(raw ?? "review").toLowerCase();
  if (["public","open","everyone"].includes(v)) return "public";
  if (["private"].includes(v)) return "private";
  return "review";
}
async function getTargetPrivacy(targetUid: string) {
  const snap = await getDoc(doc(db, "users", targetUid));
  const d = snap.exists() ? (snap.data() as any) : {};
  return resolvePrivacy(d?.follow_privacy ?? d?.preferences?.follow_privacy ?? "review");
}

export async function requestFollow(targetUid: string) {
  const meUid = auth.currentUser?.uid;
  if (!meUid) throw new Error("Not signed in");
  if (meUid === targetUid) throw new Error("Cannot follow yourself");

  const privacy = await getTargetPrivacy(targetUid);
  if (privacy === "private") throw new Error("This profile is private");

  const status: FollowStatus = privacy === "public" ? "approved" : "pending";
  const id = edgeId(meUid, targetUid);

  await setDoc(doc(db, "follows", id), {
    followerId: meUid,
    followingId: targetUid,
    status,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: false });

  return { id, status };
}

export async function approveFollowRequest(fromUid: string) {
  const meUid = auth.currentUser?.uid;
  if (!meUid) throw new Error("Not signed in");
  const id = edgeId(fromUid, meUid);
  await updateDoc(doc(db, "follows", id), { status: "approved", updatedAt: serverTimestamp() });
  return { id };
}

export async function cancelFollowRequest(targetUid: string) {
  const meUid = auth.currentUser?.uid;
  if (!meUid) throw new Error("Not signed in");
  const id = edgeId(meUid, targetUid);
  await deleteDoc(doc(db, "follows", id));
  return { id };
}

export async function denyFollow(fromUid: string) {
  const meUid = auth.currentUser?.uid;
  if (!meUid) throw new Error("Not signed in");
  const id = edgeId(fromUid, meUid);
  await deleteDoc(doc(db, "follows", id));
  return { id };
}

export async function removeFollowEdge(id: string) {
  const meUid = auth.currentUser?.uid;
  if (!meUid) throw new Error("Not signed in");
  await deleteDoc(doc(db, "follows", id));
  return { id };
}

// compat alias
export { approveFollowRequest as approveFollow };

